import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import DOMPurify from 'dompurify';
import Icon from '../../components/AppIcon';
import Image from '../../components/AppImage';
import Button from '../../components/ui/Button';
import articlesService from '../../services/articlesService';
import engagementService from '../../services/engagementService';
import { useAuth } from '../../contexts/AuthContext';
import ShareButton from './components/ShareButton';
import AdminArticleMenu from '../../components/ui/AdminArticleMenu';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import { toArabicDigits, withArabicDigits } from '../../utils/numbers';

const ArticleReading = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { user, isAuthenticated, isAdmin } = useAuth();

  const [scrollProgress, setScrollProgress] = useState(0);
  const [article, setArticle] = useState(null);
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hasClapped, setHasClapped] = useState(false);
  const [clapCount, setClapCount] = useState(0);
  const [clapLoading, setClapLoading] = useState(false);
  const [commentText, setCommentText] = useState('');
  const [replyingTo, setReplyingTo] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [commentSubmitting, setCommentSubmitting] = useState(false);
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [deletingCommentId, setDeletingCommentId] = useState(null);
  const [commentToDelete, setCommentToDelete] = useState(null);
  const [editingCommentId, setEditingCommentId] = useState(null);
  const [editCommentText, setEditCommentText] = useState('');
  const [openMenuCommentId, setOpenMenuCommentId] = useState(null);
  const [isBookmarked, setIsBookmarked] = useState(false);
  const [bookmarkLoading, setBookmarkLoading] = useState(false);

  // Scroll progress tracking
  useEffect(() => {
    const handleScroll = () => {
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement?.scrollHeight - windowHeight;
      const scrolled = window.scrollY;
      const progress = (scrolled / documentHeight) * 100;
      setScrollProgress(progress);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Fetch article data
  useEffect(() => {
    if (!id) return;

    const fetchArticle = async () => {
      setLoading(true);
      setError(null);
      try {
        const { data } = await articlesService.getArticle(id);
        setArticle(data);
        setHasClapped(data.hasUserClapped || false);
        setClapCount(data.claps || 0);
        setIsBookmarked(data.hasUserBookmarked || false);
      } catch (err) {
        setError(err.response?.data?.message || 'حدث خطأ أثناء تحميل المقال.');
      } finally {
        setLoading(false);
      }
    };

    fetchArticle();
  }, [id]);

  // Track view on mount
  useEffect(() => {
    if (!id) return;
    engagementService.trackView(id).catch(() => {
      // Silently fail — view tracking is non-critical
    });
  }, [id]);

  // Fetch comments
  const fetchComments = useCallback(async () => {
    if (!id) return;
    setCommentsLoading(true);
    try {
      const { data } = await engagementService.getComments(id);
      setComments(data || []);
    } catch {
      // Silently fail — comments loading is non-critical
    } finally {
      setCommentsLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchComments();
  }, [fetchComments]);

  // Clap toggle
  const handleClap = async () => {
    if (!isAuthenticated) {
      navigate('/signup');
      return;
    }
    if (clapLoading) return;
    setClapLoading(true);
    try {
      if (hasClapped) {
        await engagementService.removeClap(id);
        setHasClapped(false);
        setClapCount((prev) => Math.max(0, prev - 1));
      } else {
        await engagementService.clap(id);
        setHasClapped(true);
        setClapCount((prev) => prev + 1);
      }
    } catch {
      // Revert optimistic state is not needed since we await before updating
    } finally {
      setClapLoading(false);
    }
  };

  // Add comment
  const handleAddComment = async (e) => {
    e.preventDefault();
    if (!commentText.trim() || commentSubmitting) return;
    setCommentSubmitting(true);
    try {
      await engagementService.addComment(id, { content: commentText.trim() });
      setCommentText('');
      await fetchComments();
    } catch {
      // Could show error toast here
    } finally {
      setCommentSubmitting(false);
    }
  };

  // Add reply
  const handleAddReply = async (e, parentCommentId) => {
    e.preventDefault();
    if (!replyText.trim() || commentSubmitting) return;
    setCommentSubmitting(true);
    try {
      await engagementService.addComment(id, {
        content: replyText.trim(),
        parentCommentId,
      });
      setReplyText('');
      setReplyingTo(null);
      await fetchComments();
    } catch {
      // Could show error toast here
    } finally {
      setCommentSubmitting(false);
    }
  };

  const handleExit = () => {
    navigate('/homepage');
  };

  // Parse image/link value (handles JSON or plain string)
  const parseBlockValue = (value, type) => {
    if (!value) return null;
    try {
      const parsed = JSON.parse(value);
      if (type === 'image') return parsed?.url || value;
      if (type === 'link') return parsed;
      return value;
    } catch {
      return type === 'link' ? { url: value, text: value } : value;
    }
  };

  // Render a single content block based on its type
  const renderContentBlock = (block) => {
    const type = (block.type || '').toLowerCase();
    switch (type) {
      case 'text':
        return (
          <div
            key={block.sortOrder}
            className="mb-8 prose prose-lg max-w-none"
            style={{
              fontFamily: 'Amiri, serif',
              fontSize: '22px',
              lineHeight: '2.0',
              color: '#2C2B29',
              direction: 'rtl',
            }}
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(block.value || '', {
                ALLOWED_TAGS: ['p', 'h1', 'h2', 'h3', 'ul', 'ol', 'li', 'blockquote', 'strong', 'em', 'u', 's', 'a', 'br'],
                ALLOWED_ATTR: ['href', 'target', 'rel'],
              }),
            }}
          />
        );

      case 'image': {
        const src = parseBlockValue(block.value, 'image');
        if (!src) return null;
        let align = 'center';
        let caption = null;
        let width = null;
        let height = null;
        let rotate = 0;
        try {
          const parsed = JSON.parse(block.value);
          align = parsed?.align || 'center';
          caption = parsed?.caption || null;
          width = parsed?.width || null;
          height = parsed?.height || null;
          rotate = parsed?.rotate ?? 0;
        } catch {}
        const imgStyle = { maxWidth: '100%' };
        if (width) imgStyle.width = `${width}px`;
        if (height) imgStyle.height = `${height}px`;
        if (rotate) imgStyle.transform = `rotate(${rotate}deg)`;
        return (
          <figure
            key={block.sortOrder}
            className="my-10"
            style={{ textAlign: align === 'left' ? 'left' : align === 'right' ? 'right' : 'center' }}
          >
            <Image
              src={src}
              alt={caption || ''}
              className="rounded-xl max-w-full h-auto shadow-md"
              style={Object.keys(imgStyle).length ? imgStyle : undefined}
            />
            {caption && (
              <figcaption
                className="mt-2 text-gray-500 text-center"
                style={{ fontFamily: 'Amiri, serif', fontSize: '1.05rem' }}
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(caption, {
                    ALLOWED_TAGS: ['strong', 'em', 'u', 's', 'a', 'br'],
                    ALLOWED_ATTR: ['href', 'target', 'rel'],
                  }),
                }}
              />
            )}
          </figure>
        );
      }

      case 'audio': {
        const src = parseBlockValue(block.value, 'audio');
        if (!src) return null;
        return (
          <div key={block.sortOrder} className="my-10 flex justify-center">
            <audio
              controls
              src={src}
              className="w-full max-w-lg"
              style={{ direction: 'ltr' }}
            >
              متصفحك لا يدعم تشغيل الصوت.
            </audio>
          </div>
        );
      }

      case 'link': {
        const linkData = parseBlockValue(block.value, 'link');
        const href = linkData?.url || linkData;
        const text = linkData?.text || linkData || href;
        if (!href) return null;
        return (
          <div key={block.sortOrder} className="my-6 text-center">
            <a
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-5 py-3 rounded-full transition-all duration-300 hover:shadow-md"
              style={{
                fontFamily: 'Amiri, serif',
                fontSize: '18px',
                color: '#C5A059',
                backgroundColor: 'rgba(197, 160, 89, 0.08)',
                border: '1px solid rgba(197, 160, 89, 0.25)',
              }}
            >
              <Icon name="ExternalLink" size={18} color="#C5A059" />
              <span>{text}</span>
            </a>
          </div>
        );
      }

      default:
        return null;
    }
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'الآن';
    if (diffMins < 60) return `منذ ${toArabicDigits(diffMins)} دقيقة`;
    if (diffHours < 24) return `منذ ${toArabicDigits(diffHours)} ساعة`;
    if (diffDays < 30) return `منذ ${toArabicDigits(diffDays)} يوم`;
    return withArabicDigits(date.toLocaleDateString('ar-EG', { year: 'numeric', month: 'long', day: 'numeric' }));
  };

  const handleBookmarkToggle = async () => {
    if (!isAuthenticated || bookmarkLoading) return;
    setBookmarkLoading(true);
    try {
      if (isBookmarked) {
        await engagementService.removeBookmark(id);
        setIsBookmarked(false);
      } else {
        await engagementService.bookmarkArticle(id);
        setIsBookmarked(true);
      }
    } catch (err) {
      console.error('Bookmark failed:', err);
    } finally {
      setBookmarkLoading(false);
    }
  };

  const handleLikeComment = async (commentId, hasUserLiked) => {
    if (!isAuthenticated) {
      navigate('/signup');
      return;
    }
    const delta = hasUserLiked ? -1 : 1;
    const newLiked = !hasUserLiked;
    const updateInTree = (c) => {
      if (c.id === commentId) {
        return { ...c, likeCount: (c.likeCount ?? 0) + delta, hasUserLiked: newLiked };
      }
      return { ...c, replies: (c.replies ?? []).map(updateInTree) };
    };
    setComments((prev) => prev.map(updateInTree));
    try {
      if (hasUserLiked) {
        await engagementService.removeCommentLike(commentId);
      } else {
        await engagementService.likeComment(commentId);
      }
      await fetchComments();
    } catch {
      await fetchComments();
    }
  };

  const handleDeleteComment = async () => {
    if (!commentToDelete) return;
    setDeletingCommentId(commentToDelete);
    try {
      await engagementService.deleteComment(commentToDelete);
      setComments((prev) =>
        prev
          .filter((c) => c.id !== commentToDelete)
          .map((c) => ({
            ...c,
            replies: c.replies?.filter((r) => r.id !== commentToDelete) || [],
          }))
      );
    } catch (err) {
      console.error('Failed to delete comment:', err);
    } finally {
      setDeletingCommentId(null);
      setCommentToDelete(null);
    }
  };

  const handleEditComment = async (commentId, content) => {
    try {
      const { data } = await engagementService.updateComment(commentId, content);
      setComments((prev) => {
        const updateInTree = (c) => {
          if (c.id === commentId) return { ...c, content: data.content };
          return { ...c, replies: (c.replies || []).map(updateInTree) };
        };
        return prev.map(updateInTree);
      });
      setEditingCommentId(null);
      setEditCommentText('');
    } catch (err) {
      console.error('Failed to edit comment:', err);
    }
  };

  // Render a single comment
  const isCommentOwner = (c) => user?.id && String(c.userId) === String(user.id);
  const canModifyComment = (c) => isCommentOwner(c) || isAdmin;

  const renderComment = (comment, isReply = false) => (
    <div
      key={comment.id}
      className={`flex gap-3 ${isReply ? 'mr-12 mt-4' : 'mt-6'}`}
    >
      <Image
        src={comment.userAvatar}
        alt={comment.userName}
        className="w-10 h-10 rounded-full object-cover flex-shrink-0 cursor-pointer"
        onClick={() => navigate(`/user/${comment.userId}`)}
      />
      <div className="flex-1">
        <div className="flex items-center gap-2 mb-1">
          <span
            className="font-medium text-sm cursor-pointer hover:text-[#C5A059] transition-colors"
            style={{ fontFamily: 'Amiri, serif', color: '#2C2B29' }}
            onClick={() => navigate(`/user/${comment.userId}`)}
          >
            {comment.userName}
          </span>
          <span
            className="text-xs"
            style={{ fontFamily: 'Amiri, serif', color: '#9A9A9A' }}
          >
            {formatDate(comment.createdAt)}
          </span>
          {canModifyComment(comment) && (
            <div className="mr-auto flex items-center gap-2">
              {editingCommentId === comment.id ? (
                <>
                  <button
                    type="button"
                    onClick={() => {
                      setEditingCommentId(null);
                      setEditCommentText('');
                    }}
                    className="text-xs text-[#9A9A9A] hover:text-[#5A5A5A]"
                    style={{ fontFamily: 'Amiri, serif' }}
                  >
                    إلغاء
                  </button>
                  <button
                    type="button"
                    onClick={() => handleEditComment(comment.id, editCommentText)}
                    disabled={!editCommentText.trim()}
                    className="text-xs text-[#C5A059] hover:text-[#A68548] disabled:opacity-50"
                    style={{ fontFamily: 'Amiri, serif' }}
                  >
                    حفظ
                  </button>
                </>
              ) : (
                <div className="relative flex-shrink-0 overflow-visible">
                  <button
                    type="button"
                    onClick={() => setOpenMenuCommentId(openMenuCommentId === comment.id ? null : comment.id)}
                    className="p-1.5 hover:bg-[rgba(197,160,89,0.1)] rounded-lg transition-colors"
                    aria-label="خيارات التعليق"
                  >
                    <Icon name="MoreVertical" size={18} color="#9A9A9A" />
                  </button>
                  {openMenuCommentId === comment.id && (
                    <div className="absolute left-0 top-full mt-2 bg-white rounded-lg py-1.5 min-w-[120px] z-50 shadow-warm border border-[rgba(197,160,89,0.15)]">
                      <button
                        type="button"
                        onClick={() => {
                          setOpenMenuCommentId(null);
                          setEditingCommentId(comment.id);
                          setEditCommentText(comment.content || '');
                        }}
                        className="w-full px-4 py-2 text-right text-sm hover:bg-[rgba(197,160,89,0.08)] transition-colors flex items-center gap-2"
                        style={{ fontFamily: 'Amiri, serif', color: '#2C2B29' }}
                      >
                        <Icon name="Edit" size={16} color="#5A5A5A" />
                        <span>تعديل</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setOpenMenuCommentId(null);
                          setCommentToDelete(comment.id);
                        }}
                        disabled={deletingCommentId === comment.id}
                        className="w-full px-4 py-2 text-right text-sm hover:bg-[rgba(220,38,38,0.08)] transition-colors flex items-center gap-2 text-red-600 disabled:opacity-50"
                        style={{ fontFamily: 'Amiri, serif' }}
                      >
                        <Icon name="Trash2" size={16} color="currentColor" />
                        <span>حذف</span>
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
        {editingCommentId === comment.id ? (
          <div className="mb-2">
            <input
              type="text"
              value={editCommentText}
              onChange={(e) => setEditCommentText(e.target.value)}
              placeholder="عدّل تعليقك..."
              className="w-full px-4 py-2 rounded-full text-sm border focus:outline-none focus:ring-1"
              style={{
                fontFamily: 'Amiri, serif',
                borderColor: 'rgba(197, 160, 89, 0.3)',
                backgroundColor: '#FEFDFB',
                color: '#2C2B29',
              }}
              autoFocus
            />
          </div>
        ) : (
          <p
            className="text-sm leading-relaxed"
            style={{ fontFamily: 'Amiri, serif', color: '#5A5A5A' }}
          >
            {comment.content}
          </p>
        )}
        <div className="mt-2 flex items-center gap-4">
          {isAuthenticated && (
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                handleLikeComment(comment.id, comment.hasUserLiked);
              }}
              className="flex items-center gap-1 text-xs transition-colors duration-200 hover:opacity-80"
              style={{ fontFamily: 'Amiri, serif', color: comment.hasUserLiked ? '#C5A059' : '#9A9A9A' }}
            >
              <Icon
                name="Heart"
                size={14}
                color={comment.hasUserLiked ? '#C5A059' : '#9A9A9A'}
                fill={comment.hasUserLiked ? '#C5A059' : 'none'}
              />
              إعجاب ({toArabicDigits(comment.likeCount ?? 0)})
            </button>
          )}
          {!isAuthenticated && (
            <button
              type="button"
              onClick={() => navigate('/signup')}
              className="flex items-center gap-1 text-xs transition-colors duration-200 hover:opacity-80"
              style={{ fontFamily: 'Amiri, serif', color: '#9A9A9A' }}
            >
              <Icon name="Heart" size={14} color="#9A9A9A" />
              إعجاب ({toArabicDigits(comment.likeCount ?? 0)})
            </button>
          )}
          {isAuthenticated && !isReply && (
            <button
              onClick={() => {
                setReplyingTo(replyingTo === comment.id ? null : comment.id);
                setReplyText('');
              }}
              className="text-xs transition-colors duration-200 hover:opacity-80"
              style={{ fontFamily: 'Amiri, serif', color: '#C5A059' }}
            >
              رد
            </button>
          )}
        </div>
        {/* Reply form */}
        {replyingTo === comment.id && (
          <form
            onSubmit={(e) => handleAddReply(e, comment.id)}
            className="mt-3 flex gap-2"
          >
            <input
              type="text"
              value={replyText}
              onChange={(e) => setReplyText(e.target.value)}
              placeholder="اكتب ردك..."
              className="flex-1 px-4 py-2 rounded-full text-sm border focus:outline-none focus:ring-1"
              style={{
                fontFamily: 'Amiri, serif',
                borderColor: 'rgba(197, 160, 89, 0.3)',
                backgroundColor: '#FEFDFB',
                color: '#2C2B29',
              }}
              disabled={commentSubmitting}
            />
            <button
              type="submit"
              disabled={commentSubmitting || !replyText.trim()}
              className="px-4 py-2 rounded-full text-sm transition-all duration-300 disabled:opacity-50"
              style={{
                fontFamily: 'Amiri, serif',
                backgroundColor: '#C5A059',
                color: 'white',
              }}
            >
              {commentSubmitting ? '...' : 'رد'}
            </button>
          </form>
        )}
        {/* Nested replies */}
        {comment.replies?.length > 0 && (
          <div>
            {comment.replies.map((reply) => renderComment(reply, true))}
          </div>
        )}
      </div>
    </div>
  );

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-[#F9F7F2] flex items-center justify-center">
        <div className="text-center">
          <div
            className="inline-block w-10 h-10 border-3 border-t-transparent rounded-full animate-spin mb-4"
            style={{ borderColor: 'rgba(197, 160, 89, 0.3)', borderTopColor: '#C5A059' }}
          />
          <p
            className="text-lg"
            style={{ fontFamily: 'Amiri, serif', color: '#5A5A5A' }}
          >
            جارٍ تحميل المقال...
          </p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-[#F9F7F2] flex items-center justify-center">
        <div className="text-center max-w-md px-6">
          <Icon name="AlertCircle" size={48} color="#C5A059" className="mx-auto mb-4" />
          <p
            className="text-lg mb-6"
            style={{ fontFamily: 'Amiri, serif', color: '#5A5A5A' }}
          >
            {error}
          </p>
          <button
            onClick={() => navigate('/homepage')}
            className="px-6 py-3 rounded-full transition-all duration-300"
            style={{
              backgroundColor: '#C5A059',
              color: 'white',
              fontFamily: 'Amiri, serif',
              fontSize: '16px',
              fontWeight: 600,
            }}
          >
            العودة للصفحة الرئيسية
          </button>
        </div>
      </div>
    );
  }

  if (!article) return null;

  // Sort content blocks by sortOrder
  const sortedContent = [...(article.content || [])].sort(
    (a, b) => a.sortOrder - b.sortOrder
  );

  return (
    <div className="min-h-screen bg-[#F9F7F2] relative">
      {/* Progress Bar */}
      <div
        className="fixed top-0 left-0 h-[2px] bg-[#C5A059] z-50 transition-all duration-150"
        style={{ width: `${scrollProgress}%` }}
      />

      {/* Floating Exit Button */}
      <button
        onClick={handleExit}
        className="fixed top-10 right-10 z-40 w-14 h-14 rounded-full bg-[rgba(197,160,89,0.1)] hover:bg-[rgba(197,160,89,0.2)] transition-all duration-300 flex items-center justify-center group"
        aria-label="العودة للصفحة الرئيسية"
      >
        <Icon name="X" size={24} color="#C5A059" />
      </button>

      {/* Floating Share Button */}
      <ShareButton articleId={id} articleTitle={article.title} />

      {/* Floating Bookmark Button */}
      {isAuthenticated && (
        <button
          onClick={handleBookmarkToggle}
          disabled={bookmarkLoading}
          className="fixed top-28 left-10 z-40 w-14 h-14 rounded-full bg-[rgba(197,160,89,0.1)] hover:bg-[rgba(197,160,89,0.2)] transition-all duration-300 flex items-center justify-center group"
          aria-label={isBookmarked ? 'إزالة الحفظ' : 'حفظ المقال'}
        >
          <Icon
            name="Bookmark"
            size={24}
            color="#C5A059"
            fill={isBookmarked ? '#C5A059' : 'none'}
            className={bookmarkLoading ? 'opacity-50' : ''}
          />
        </button>
      )}

      {/* Admin Article Menu */}
      <div className="fixed top-44 left-10 z-40">
        <AdminArticleMenu
          articleId={id}
          onDeleteSuccess={() => navigate('/homepage')}
        />
      </div>

      {/* Comment Delete Confirmation */}
      <ConfirmDialog
        isOpen={!!commentToDelete}
        onConfirm={handleDeleteComment}
        onCancel={() => setCommentToDelete(null)}
        title="حذف التعليق"
        message="هل أنت متأكد من حذف هذا التعليق؟"
        loading={!!deletingCommentId}
      />

      {/* Main Content Container */}
      <main className="max-w-[720px] mx-auto px-6 py-20">
        {/* Category Pill */}
        <div className="text-center mb-6">
          <span
            className="inline-block px-4 py-2 rounded-full text-sm"
            style={{
              fontFamily: 'Amiri, serif',
              color: '#C5A059',
              backgroundColor: 'rgba(197, 160, 89, 0.1)',
            }}
          >
            {article.circle}
          </span>
        </div>

        {/* Title */}
        <h1
          className="text-center mb-8 leading-tight"
          style={{
            fontFamily: 'Amiri, serif',
            fontSize: '48px',
            color: '#2C2B29',
            fontWeight: 700,
          }}
        >
          {article.title}
        </h1>

        {/* Author Section */}
        <div className="flex items-center justify-center gap-4 mb-12">
          <Image
            src={article.authorImage}
            alt={article.author}
            className="w-16 h-16 rounded-full object-cover shadow-lg cursor-pointer"
            onClick={() => navigate(`/user/${article.authorId}`)}
          />
          <div className="text-right">
            <p
              className="text-lg font-medium mb-1 cursor-pointer hover:text-[#C5A059] transition-colors"
              style={{ fontFamily: 'Amiri, serif', color: '#2C2B29' }}
              onClick={() => navigate(`/user/${article.authorId}`)}
            >
              {article.author}
            </p>
            <div
              className="flex items-center gap-2 text-sm"
              style={{ fontFamily: 'Amiri, serif', color: '#5A5A5A' }}
            >
              <span>{formatDate(article.publishedDate)}</span>
              <span>•</span>
              <span>{withArabicDigits(article.readTime ?? '')}</span>
            </div>
          </div>
        </div>

        {/* Hero Art - Perfect Circle */}
        {article.image && (
          <div className="flex justify-center mb-16">
            <div
              className="rounded-full overflow-hidden"
              style={{
                width: '400px',
                height: '400px',
                boxShadow: '0 20px 60px rgba(139, 105, 20, 0.15)',
              }}
            >
              <Image
                src={article.image}
                alt={article.imageAlt || article.title}
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        )}

        {/* Body Content — rendered from content blocks */}
        <div className="prose prose-lg max-w-none mb-16">
          {sortedContent.map((block) => renderContentBlock(block))}
        </div>

        {/* Article Stats */}
        <div
          className="flex items-center justify-center gap-6 mb-8 text-sm"
          style={{ fontFamily: 'Amiri, serif', color: '#9A9A9A' }}
        >
          {article.views != null && (
            <span className="flex items-center gap-1">
              <Icon name="Eye" size={16} color="#9A9A9A" />
              {toArabicDigits(article.views)} مشاهدة
            </span>
          )}
          {article.commentCount != null && (
            <span className="flex items-center gap-1">
              <Icon name="MessageCircle" size={16} color="#9A9A9A" />
              {toArabicDigits(article.commentCount)} تعليق
            </span>
          )}
          <span className="flex items-center gap-1">
            <Icon name="Heart" size={16} color="#9A9A9A" />
            {toArabicDigits(clapCount)} إعجاب
          </span>
          {article.shareCount != null && article.shareCount > 0 && (
            <span className="flex items-center gap-1">
              <Icon name="Share2" size={16} color="#9A9A9A" />
              {toArabicDigits(article.shareCount)} مشاركة
            </span>
          )}
          <button
            onClick={() => navigate(`/article/${id}/engagement`)}
            className="flex items-center gap-1 hover:text-[#C5A059] transition-colors"
          >
            <Icon name="MessageCircle" size={16} color="#9A9A9A" />
            <span>تفاعل مع المجتمع</span>
          </button>
        </div>

        {/* Clap/Like Button */}
        <div className="flex justify-center mb-12">
          <button
            onClick={handleClap}
            disabled={clapLoading}
            className="flex items-center gap-3 px-8 py-4 rounded-full transition-all duration-300 hover:shadow-lg disabled:opacity-60"
            style={{
              backgroundColor: hasClapped
                ? 'rgba(197, 160, 89, 0.2)'
                : 'rgba(197, 160, 89, 0.1)',
              border: '2px solid #C5A059',
              fontFamily: 'Amiri, serif',
              fontSize: '18px',
              color: '#C5A059',
            }}
          >
            <Icon
              name={hasClapped ? 'HeartOff' : 'Heart'}
              size={24}
              color="#C5A059"
            />
            <span>{hasClapped ? 'إلغاء الإعجاب' : 'أعجبني'}</span>
            <span
              className="mr-1 px-2 py-0.5 rounded-full text-sm"
              style={{ backgroundColor: 'rgba(197, 160, 89, 0.15)' }}
            >
              {toArabicDigits(clapCount)}
            </span>
          </button>
        </div>

        {/* Comments Section */}
        <div
          className="p-8 rounded-2xl mb-8"
          style={{ backgroundColor: 'rgba(197, 160, 89, 0.03)' }}
        >
          <h2
            className="mb-6"
            style={{
              fontFamily: 'Aref Ruqaa, serif',
              fontSize: '28px',
              color: '#2C2B29',
              fontWeight: 700,
            }}
          >
            التعليقات
          </h2>

          {/* Comment Form — authenticated users only */}
          {isAuthenticated ? (
            <form onSubmit={handleAddComment} className="mb-8">
              <div className="flex gap-3">
                <Image
                  src={user?.avatarUrl}
                  alt={user?.fullName || 'أنت'}
                  className="w-10 h-10 rounded-full object-cover flex-shrink-0"
                />
                <div className="flex-1">
                  <textarea
                    value={commentText}
                    onChange={(e) => setCommentText(e.target.value)}
                    placeholder="أضف تعليقك..."
                    rows={3}
                    className="w-full px-4 py-3 rounded-xl text-sm border resize-none focus:outline-none focus:ring-1"
                    style={{
                      fontFamily: 'Amiri, serif',
                      fontSize: '16px',
                      borderColor: 'rgba(197, 160, 89, 0.3)',
                      backgroundColor: '#FEFDFB',
                      color: '#2C2B29',
                    }}
                    disabled={commentSubmitting}
                  />
                  <div className="flex justify-end mt-2">
                    <button
                      type="submit"
                      disabled={commentSubmitting || !commentText.trim()}
                      className="px-6 py-2 rounded-full text-sm transition-all duration-300 disabled:opacity-50"
                      style={{
                        fontFamily: 'Amiri, serif',
                        backgroundColor: '#C5A059',
                        color: 'white',
                        fontWeight: 600,
                      }}
                    >
                      {commentSubmitting ? 'جارٍ الإرسال...' : 'أضف تعليق'}
                    </button>
                  </div>
                </div>
              </div>
            </form>
          ) : (
            <div
              className="text-center py-6 mb-6 rounded-xl"
              style={{ backgroundColor: 'rgba(197, 160, 89, 0.06)' }}
            >
              <p
                className="mb-3"
                style={{ fontFamily: 'Amiri, serif', fontSize: '16px', color: '#5A5A5A' }}
              >
                سجّل دخولك لإضافة تعليق
              </p>
              <button
                onClick={() => navigate('/signup')}
                className="px-5 py-2 rounded-full text-sm transition-all duration-300"
                style={{
                  fontFamily: 'Amiri, serif',
                  backgroundColor: '#C5A059',
                  color: 'white',
                  fontWeight: 600,
                }}
              >
                تسجيل الدخول
              </button>
            </div>
          )}

          {/* Comments List */}
          {commentsLoading ? (
            <div className="text-center py-8">
              <div
                className="inline-block w-6 h-6 border-2 border-t-transparent rounded-full animate-spin"
                style={{ borderColor: 'rgba(197, 160, 89, 0.3)', borderTopColor: '#C5A059' }}
              />
            </div>
          ) : comments.length > 0 ? (
            <div>
              {comments.map((comment) => renderComment(comment))}
            </div>
          ) : (
            <p
              className="text-center py-6"
              style={{ fontFamily: 'Amiri, serif', fontSize: '16px', color: '#9A9A9A' }}
            >
              لا توجد تعليقات بعد. كن أول من يعلّق!
            </p>
          )}
        </div>

        {/* Next Article Suggestion */}
        <div
          className="p-8 rounded-2xl text-center"
          style={{ backgroundColor: 'rgba(197, 160, 89, 0.05)' }}
        >
          <p
            className="mb-4"
            style={{
              fontFamily: 'Amiri, serif',
              fontSize: '18px',
              color: '#5A5A5A',
            }}
          >
            هل أعجبك المقال؟
          </p>
          <button
            onClick={() => navigate('/homepage')}
            className="px-6 py-3 rounded-full transition-all duration-300"
            style={{
              backgroundColor: '#C5A059',
              color: 'white',
              fontFamily: 'Amiri, serif',
              fontSize: '16px',
              fontWeight: 600,
            }}
          >
            اكتشف المزيد من المقالات
          </button>
        </div>
      </main>
    </div>
  );
};

export default ArticleReading;
